/**
 * Chrome every authenticated shell mounts, whichever mode it is in: the consent gate, the
 * overage warning, the account-switched toast, the live credit usage, the impersonation and
 * exhausted-credits banners and the confirmation of an account switch picked in the profile menu.
 */
import {
  ConsentGateModal,
  ImpersonationBanner,
  isConsentPending,
  isGdprConsentPending,
  SwitchAccountConfirmModal,
  useAccountSwitchedToast,
  useAuth
} from '@/features/auth';
import { CreditsExhaustedBanner, useCreditUsageLive } from '@/features/credits';
import { useOnboardingPending } from '@/features/onboarding';
import { useOverageNotification } from '@/hooks/use-overage-notification';
import type { AccountSwitchPrompt } from './use-profile-menu';

function OverageNotificationGuard() {
  useOverageNotification();
  return null;
}

/** The one `creditUsageChanged` listener of the session; it keeps the cached credit usage current. */
function CreditUsageLiveGuard() {
  useCreditUsageLive();
  return null;
}

/** Names the organization the session just entered, once, after an account switch reloaded. */
function AccountSwitchedGuard() {
  useAccountSwitchedToast();
  return null;
}

/**
 * Blocks the whole app behind the mandatory TOS/PP + GDPR consent modal; the GDPR decision waits
 * until onboarding is done. Suppressed during impersonation.
 */
function ConsentGateGuard() {
  const { user } = useAuth();
  // Ignores the policies, so a sign-up accepting them here already knows onboarding comes next.
  const onboardingDue = useOnboardingPending({ ignorePolicies: true });
  const gdprDue = isGdprConsentPending(user) && !onboardingDue;
  if (!isConsentPending(user) && !gdprDue) return null;
  return <ConsentGateModal deferGdpr={onboardingDue} />;
}

/** Guards and banners shared by both shells, in the order they sit above the page. */
export function SessionChrome() {
  return (
    <>
      <ConsentGateGuard />
      <OverageNotificationGuard />
      <AccountSwitchedGuard />
      <CreditUsageLiveGuard />
      <ImpersonationBanner />
      <CreditsExhaustedBanner />
    </>
  );
}

/**
 * Confirmation of an account picked in the profile menu. Kept outside the menu on purpose —
 * picking an organization closes the popover/drawer the switcher lives in, which would take the
 * confirmation with it.
 */
export function AccountSwitchConfirm({ prompt }: { prompt: AccountSwitchPrompt }) {
  const { activeAccount, pendingAccount, switching, confirmSwitch, cancelSwitch } = prompt;
  if (!pendingAccount) return null;

  return (
    <SwitchAccountConfirmModal
      fromName={activeAccount?.name ?? ''}
      toName={pendingAccount.name}
      loading={switching}
      onConfirm={() => void confirmSwitch()}
      onClose={cancelSwitch}
    />
  );
}
